// Objectives (EDU-3, ARCH §29.4). PURE (ENG-1).
//
// `compileCriterion` turns a lesson's data (`registry/schemas.ts` `CriterionData`)
// into a working `LessonCriterion` onto the advancement engine's own `Criterion`
// shape (`game/advancements.ts`) — one evaluator serves both. `ObjectiveTracker` is
// a strict generalization of `AdvancementTracker`: counted criteria (`need` may be
// > 1) and an `after` completion gate, on top of the same event-driven `fire`.
import { EDU } from '../config';
import type { Trigger } from '../game/advancements';
import type { CriterionData, EduTriggerKind, ObjectiveDef, RegionDef } from '../registry/schemas';

export type { CriterionData, EduTriggerKind, ObjectiveDef, RegionDef } from '../registry/schemas';

/** One compiled criterion (mirrors `game/advancements.ts` `Criterion`, plus a
 *  count target). `id` is content-derived (trigger kind + its identifying
 *  fields) — stable across a lesson reload, and namespaced per-objective by
 *  `ObjectiveTracker` before it ever reaches `counts` (two objectives naming the
 *  same fact, e.g. two `notebook_record` criteria for `chicken` with different
 *  `count`s, must not share a counter). */
export interface LessonCriterion {
  readonly id: string;
  readonly test: (t: Trigger) => boolean;
  readonly need: number;
}

/** Every numeric field a trigger kind carries — the ONLY field names a
 *  criterion's `min`/`max` may bound (§29.4: "a bound on a field the kind lacks
 *  fails at load"). `autopilot_hold` has two (§29.12 "tune-the-autopilot" bounds
 *  both `error` and `ticks` from the SAME firing), which is why bounds are a
 *  field-keyed map rather than one fixed slot. */
const NUMERIC_FIELDS: Readonly<Record<EduTriggerKind, readonly string[]>> = {
  instrument_read: ['value'],
  notebook_record: [],
  region_entered: [],
  mob_bred: [],
  crop_harvested: ['produce'],
  beacon_found: [],
  aircraft_landed: [],
  autopilot_hold: ['ticks', 'error'],
  population_count: ['value'],
  capacity_message: [],
};

/** The content-derived id `compileCriterion` gives a criterion (exported so a
 *  host/UI can compute the SAME key `ObjectiveTracker.counts` uses, e.g. the
 *  lesson screen's `k / n` readout, without duplicating this switch). */
export function criterionId(c: CriterionData): string {
  switch (c.trigger) {
    case 'instrument_read': return `instrument_read:${c.instrument}:${c.metric}`;
    case 'notebook_record': return `notebook_record:${c.species}`;
    case 'region_entered': return `region_entered:${c.region}`;
    case 'mob_bred': return `mob_bred:${c.species}:${c.recordedParents ?? false}`;
    case 'crop_harvested': return `crop_harvested:${c.crop}`;
    case 'beacon_found': return 'beacon_found';
    case 'aircraft_landed': return `aircraft_landed:${c.runway ?? false}`;
    case 'autopilot_hold': return 'autopilot_hold';
    case 'population_count': return `population_count:${c.species}`;
    case 'capacity_message': return 'capacity_message';
  }
}

/** True when every field a criterion bounds is within its `min`/`max` on the
 *  fired trigger. Assumes the bound keys were already validated against
 *  `NUMERIC_FIELDS` (`compileCriterion` does that once, at compile time — never
 *  per firing). */
function withinBounds(c: CriterionData, t: Record<string, unknown>): boolean {
  for (const [key, min] of Object.entries(c.min ?? {})) if ((t[key] as number) < min) return false;
  for (const [key, max] of Object.entries(c.max ?? {})) if ((t[key] as number) > max) return false;
  return true;
}

function matchesIdentity(c: CriterionData, t: Trigger): boolean {
  switch (c.trigger) {
    case 'instrument_read':
      return t.kind === 'instrument_read' && t.instrument === c.instrument && t.metric === c.metric;
    case 'notebook_record':
      return t.kind === 'notebook_record' && t.species === c.species;
    case 'region_entered':
      return t.kind === 'region_entered' && t.region === c.region;
    case 'mob_bred':
      return t.kind === 'mob_bred' && t.species === c.species
        && (c.recordedParents === undefined || t.recordedParents === c.recordedParents);
    case 'crop_harvested':
      return t.kind === 'crop_harvested' && t.crop === c.crop;
    case 'beacon_found':
      return t.kind === 'beacon_found';
    case 'aircraft_landed':
      return t.kind === 'aircraft_landed' && (c.runway === undefined || t.runway === c.runway);
    case 'autopilot_hold':
      return t.kind === 'autopilot_hold';
    case 'population_count':
      return t.kind === 'population_count' && t.species === c.species;
    case 'capacity_message':
      return t.kind === 'capacity_message';
  }
}

/** Build a working `LessonCriterion` from data. Throws — naming the criterion —
 *  for an unknown trigger kind or a bound naming a field the kind does not
 *  carry; both are load-time mistakes, never a play-time surprise (§29.4). */
export function compileCriterion(c: CriterionData): LessonCriterion {
  const fields = NUMERIC_FIELDS[c.trigger];
  if (fields === undefined) throw new Error(`compileCriterion: unknown trigger '${String((c as { trigger: unknown }).trigger)}'`);
  const id = criterionId(c);
  for (const key of [...Object.keys(c.min ?? {}), ...Object.keys(c.max ?? {})]) {
    if (!fields.includes(key)) throw new Error(`criterion '${id}': bound names field '${key}', which '${c.trigger}' does not carry`);
  }
  const test = (t: Trigger): boolean => matchesIdentity(c, t) && withinBounds(c, t as unknown as Record<string, unknown>);
  return { id, test, need: c.count ?? 1 };
}

export interface ObjectiveSave {
  readonly done: readonly string[];
  readonly counts: readonly (readonly [string, number])[];
}

interface CompiledObjective {
  readonly criteria: readonly LessonCriterion[];
  readonly after: string | undefined;
}

/**
 * Tracks a player's progress through one lesson's objectives (§29.4). `fire`
 * returns the names newly completed by that trigger — idempotent once an
 * objective is done, and gated: an objective with `after` never completes
 * before its predecessor, even if its own criteria were already met.
 */
export class ObjectiveTracker {
  readonly done = new Set<string>();
  /** `${objectiveName}::${criterion.id}` → times satisfied so far. */
  readonly counts = new Map<string, number>();
  private readonly compiled = new Map<string, CompiledObjective>();

  constructor(private readonly objectives: readonly ObjectiveDef[]) {
    for (const o of objectives) {
      this.compiled.set(o.name, { criteria: o.criteria.map(compileCriterion), after: o.after });
    }
  }

  private key(objName: string, critId: string): string {
    return `${objName}::${critId}`;
  }

  fire(t: Trigger): string[] {
    const newly: string[] = [];
    for (const o of this.objectives) {
      if (this.done.has(o.name)) continue;
      const compiled = this.compiled.get(o.name)!;
      let allMet = true;
      for (const crit of compiled.criteria) {
        const k = this.key(o.name, crit.id);
        let count = this.counts.get(k) ?? 0;
        if (crit.test(t)) {
          count++;
          this.counts.set(k, count);
        }
        if (count < crit.need) allMet = false;
      }
      if (allMet && (compiled.after === undefined || this.done.has(compiled.after))) {
        this.done.add(o.name);
        newly.push(o.name);
      }
    }
    return newly;
  }

  toJSON(): ObjectiveSave {
    return { done: [...this.done], counts: [...this.counts] };
  }

  load(save: ObjectiveSave | null | undefined): void {
    this.done.clear();
    this.counts.clear();
    if (!save) return;
    for (const name of save.done) this.done.add(name);
    for (const [k, v] of save.counts) this.counts.set(k, v);
  }
}

// ── Regions (EDU-3, §29.4; D-TF-194) — the only non-event objective path ────────

/** Per-player containment state for one lesson's regions — which region names the
 *  player was inside as of the last check (so a transition can be detected). */
export interface RegionState {
  readonly inside: ReadonlySet<string>;
}

export function emptyRegionState(): RegionState {
  return { inside: new Set() };
}

/** Pure AABB containment test. Exported (rather than inlined in `regionCheck`) so
 *  the "no regions declared ⇒ no check runs" and "at most `ticks/
 *  EDU.REGION_CHECK_TICKS` times" claims are spy-observable — both are really the
 *  same claim, that this function is the only per-region work `regionCheck` does. */
export function regionsInside(regions: readonly RegionDef[], x: number, y: number, z: number): Set<string> {
  const out = new Set<string>();
  for (const r of regions) {
    if (x >= r.min[0] && x <= r.max[0] && y >= r.min[1] && y <= r.max[1] && z >= r.min[2] && z <= r.max[2]) out.add(r.name);
  }
  return out;
}

/** §29.4's region check. Cheap enough to call every tick — it tests containment at
 *  most once per `EDU.REGION_CHECK_TICKS`, and never at all with no declared
 *  regions — and returns the (possibly unchanged) state plus the `region_entered`
 *  triggers an outside→inside transition fired this call. Leaving and re-entering
 *  fires again: `state` only ever reflects the LAST check, never history. */
export function regionCheck(
  regions: readonly RegionDef[], pos: { x: number; y: number; z: number }, state: RegionState, tick: number,
): { state: RegionState; fired: Trigger[] } {
  if (regions.length === 0 || tick % EDU.REGION_CHECK_TICKS !== 0) return { state, fired: [] };
  const nowInside = regionsInside(regions, pos.x, pos.y, pos.z);
  const fired: Trigger[] = [];
  for (const name of nowInside) if (!state.inside.has(name)) fired.push({ kind: 'region_entered', region: name });
  return { state: { inside: nowInside }, fired };
}
